import React, { FC, ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import Badge, { PifBadgeVariant } from "../../../../assets/hamaspik/Badge";
import useFitToViewport from "./useFitToViewport";
import "./PifDoneScreen.scss";

export type PifDoneTone = "success" | "warning" | "info";
export type PifDoneChainState = "done" | "current" | "waiting";

export interface PifDoneChainItem {
  name: string;
  detail: string;
  state: PifDoneChainState;
}

export interface PifDoneRow {
  k: string;
  v: string;
}

export interface PifDoneStep {
  title: string;
  body: string;
}

export interface PifDoneAction {
  label: string;
  variant: "outlined" | "contained";
  onClick: () => void;
  testId: string;
}

interface PifDoneScreenProps {
  icon: ReactNode;
  tone: PifDoneTone;
  title: string;
  lede: string;
  meta?: string;
  chain?: PifDoneChainItem[];
  receipt?: PifDoneRow[];
  nextSteps?: PifDoneStep[];
  actions: PifDoneAction[];
}

const CHAIN_BADGE_LABEL: Record<PifDoneChainState, string> = { done: "Signed", current: "Pending", waiting: "Waiting" };
const CHAIN_BADGE_VARIANT: Record<PifDoneChainState, PifBadgeVariant> = { done: "success", current: "warning", waiting: "neutral" };

const PifDoneScreen: FC<PifDoneScreenProps> = ({ icon, tone, title, lede, meta, chain, receipt, nextSteps, actions }) => {
  const [fitRef, fitStyle] = useFitToViewport<HTMLDivElement>();

  return (
    <Box className="pif-done" ref={fitRef} style={fitStyle}>
      <Box className="pif-done__inner">
        <Box className={`pif-done__icon pif-done__icon--${tone}`} aria-hidden>
          {icon}
        </Box>
        <Typography className="pif-done__title">{title}</Typography>
        <Typography className="pif-done__lede">{lede}</Typography>
        {meta && <Typography className="pif-done__meta">{meta}</Typography>}

        {chain && (
          <Box className="pif-done__chain">
            {chain.map((c, index) => (
              <Box key={c.name} className="pif-done__chain-row">
                <Box
                  className={`pif-done__chain-dot${c.state === "done" ? " pif-done__chain-dot--done" : c.state === "current" ? " pif-done__chain-dot--next" : ""}`}
                >
                  {c.state === "done" ? <CheckRoundedIcon sx={{ fontSize: 14 }} /> : index + 1}
                </Box>
                <Box className="pif-done__chain-body">
                  <Typography className="pif-done__chain-name">{c.name}</Typography>
                  <Typography className="pif-done__chain-detail">{c.detail}</Typography>
                </Box>
                <Badge variant={CHAIN_BADGE_VARIANT[c.state]} size="sm">
                  {CHAIN_BADGE_LABEL[c.state]}
                </Badge>
              </Box>
            ))}
          </Box>
        )}

        {/* One row so the two cards can sit side by side where there's width for them — stacked
            they made this screen taller than the space it has and the page scrolled. */}
        {(receipt || nextSteps) && (
          <Box className="pif-done__cards">
            {receipt && (
              <Box className="pif-done__card">
                <Typography className="pif-done__card-heading">RECEIPT</Typography>
                {receipt.map((r) => (
                  <Box key={r.k} className="pif-done__card-row">
                    <Typography className="pif-done__card-k">{r.k}</Typography>
                    <Typography className="pif-done__card-v">{r.v}</Typography>
                  </Box>
                ))}
              </Box>
            )}

            {nextSteps && (
              <Box className="pif-done__card">
                <Typography className="pif-done__card-heading">WHAT HAPPENS NEXT</Typography>
                <Box className="pif-done__steps">
                  {nextSteps.map((s, index) => (
                    <Box key={s.title} className="pif-done__step">
                      <Box className={`pif-done__step-dot${index === 0 ? ` pif-done__step-dot--${tone}` : ""}`}>
                        {index === 0 ? <CheckRoundedIcon sx={{ fontSize: 13 }} /> : index + 1}
                      </Box>
                      <Box>
                        <Typography className="pif-done__step-title">{s.title}</Typography>
                        <Typography className="pif-done__step-body">{s.body}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        <Box className="pif-done__actions">
          {actions.map((a) => (
            <Button key={a.label} variant={a.variant} size="small" onClick={a.onClick} data-testid={a.testId}>
              {a.label}
            </Button>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default PifDoneScreen;
